import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheck } from '@fortawesome/free-solid-svg-icons';

import theme from '@/shared/theme/index.module.scss';
import notificationApi from '@/core/notifications/api/notification-api';
import { ContactVerificationPreview } from '@/core/notifications/model';
import { useOpenWidget } from '@/core/widget-navigation/export';

type PageState = 'loading' | 'preview' | 'confirming' | 'confirmed' | 'expired' | 'error';

export default function VerifyContactPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<PageState>(token ? 'loading' : 'error');
  const [preview, setPreview] = useState<ContactVerificationPreview | null>(null);
  const openWidget = useOpenWidget();

  useEffect(() => {
    if (!token) return;
    notificationApi
      .previewContactVerification(token)
      .then(result => {
        setPreview(result);
        setState('preview');
      })
      .catch((error: any) => {
        setState(error?.status === 410 ? 'expired' : 'error');
      });
  }, [token]);

  const handleConfirm = () => {
    if (!token) return;
    setState('confirming');
    notificationApi
      .confirmContactVerification(token)
      .then(() => setState('confirmed'))
      .catch((error: any) => {
        setState(error?.status === 410 ? 'expired' : 'error');
      });
  };

  const channelName = preview?.channelDisplayName ?? '';

  return (
    <div className="d-flex justify-content-center">
      <div className="card m-5" style={{ maxWidth: 480, width: '100%' }}>
        <div className="card-body bg-dark text-center">
          {state === 'loading' && (
            <div className="m-3">
              <BarLoader color={theme.primary} width={'100%'} />
            </div>
          )}

          {(state === 'preview' || state === 'confirming') && preview && (
            <>
              <FontAwesomeIcon icon={faBell} size="2x" className="mb-3" />
              <h5>Confirm your {channelName} contact</h5>
              <p
                className="text-truncate d-block mx-auto"
                style={{ maxWidth: '100%' }}
                title={preview.identityValue}
              >
                {preview.identityValue}
              </p>
              <p className="text-muted">
                Confirm that you want to receive notifications at this address.
              </p>
              <button
                className="btn btn-primary"
                disabled={state === 'confirming'}
                onClick={handleConfirm}
              >
                {state === 'confirming' ? 'Confirming…' : 'Confirm'}
              </button>
            </>
          )}

          {state === 'confirmed' && (
            <>
              <FontAwesomeIcon icon={faCheck} size="2x" className="mb-3 text-success" />
              <h5>All set!</h5>
              <p className="text-muted">
                Your contact was verified successfully. You'll receive a message via {channelName}{' '}
                confirming everything is set up.
              </p>
            </>
          )}

          {state === 'expired' && (
            <>
              <h5>This link has expired or was already used</h5>
              <p className="text-muted">
                Go back to Notification Settings to add the contact again.
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={async () => {
                  const result = await openWidget('settings', 'notifications/contacts');
                  if (!result.ok) window.location.assign('/login');
                }}
              >
                Go to Notification Settings
              </button>
            </>
          )}

          {state === 'error' && (
            <>
              <h5>We couldn't verify this contact</h5>
              <p className="text-muted">
                This link is invalid. Try again from Notification Settings.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
